import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import HelpModal from '../../../components/HelpModal/HelpModal'
import milestoneService from '../../../services/milestoneService'
import projectService from '../../../services/projectService'
import employeeMasterService from '../../../services/employeeMasterService'
import priorityMasterService from '../../../services/priorityMasterService'
import autoIncrementService from '../../../services/autoIncrementService'
import { 
  Target,
  Calendar,
  User,
  FileText,
  DollarSign,
  AlertCircle,
  RotateCcw,
  FlaskConical,
  HelpCircle
} from 'lucide-react'

const MilestoneForm = ({ mode = 'create', milestoneId = null, initialData = null, breadcrumbItems: customBreadcrumbItems = null }) => {
  const navigate = useNavigate()
  const [showHelp, setShowHelp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [milestoneDataLoading, setMilestoneDataLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [masterDataLoaded, setMasterDataLoaded] = useState(false)
  const [isLoadingMilestoneNumber, setIsLoadingMilestoneNumber] = useState(false)
  const hasLoadedMilestoneDataRef = useRef(false)
  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'
  const isReadOnly = isViewMode

  const [masterData, setMasterData] = useState({
    projects: [],
    employees: [],
    priorities: [],
    milestones: [] // For dependencies and parent milestone
  })

  const [formData, setFormData] = useState({
    milestoneNumber: '',
    milestoneName: '',
    milestoneDescription: '',
    project: '',
    assignedTo: '',
    status: 'Not Started',
    priority: '',
    plannedDate: '',
    actualDate: '',
    startDate: '',
    completionDate: '',
    progressPercentage: 0,
    estimatedHours: 0,
    actualHours: 0,
    isBillable: true,
    billingAmount: 0,
    currency: 'INR',
    invoicedAmount: 0,
    paidAmount: 0,
    dependencies: [],
    parentMilestone: '',
    deliverables: [],
    acceptanceCriteria: [],
    notes: '',
    requiresApproval: false,
    approver: '',
    riskLevel: 'Low'
  })

  const [errors, setErrors] = useState({})
  const [newDeliverable, setNewDeliverable] = useState('')
  const [newAcceptanceCriteria, setNewAcceptanceCriteria] = useState('')

  // Load master data on component mount
  useEffect(() => {
    if (!masterDataLoaded && !loading) {
      loadMasterData()
    }
  }, [masterDataLoaded, loading])

  // Fetch next milestone number (only for new milestones)
  useEffect(() => {
    if (!isEditMode && !isViewMode && !isLoadingMilestoneNumber) {
      fetchNextMilestoneNumber()
    }
  }, [isEditMode, isViewMode])

  // Load milestone data for edit/view mode - wait for master data to be loaded first
  useEffect(() => {
    if ((isEditMode || isViewMode) && milestoneId && masterDataLoaded && !hasLoadedMilestoneDataRef.current) {
      if (initialData) {
        populateFormData(initialData)
        hasLoadedMilestoneDataRef.current = true
      } else if (!milestoneDataLoading) {
        loadMilestoneData()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [milestoneId, isEditMode, isViewMode, masterDataLoaded])

  const loadMilestoneData = async () => {
    if (hasLoadedMilestoneDataRef.current) {
      return
    }
    
    try {
      setMilestoneDataLoading(true)
      hasLoadedMilestoneDataRef.current = true
      const response = await milestoneService.getMilestoneById(milestoneId)
      
      if (response.success && response.data) {
        populateFormData(response.data)
      } else {
        toast.error(response.message || 'Failed to load milestone')
        navigate('/projects/milestones/list')
      }
    } catch (error) {
      console.error('Error loading milestone:', error)
      toast.error('Failed to load milestone')
      navigate('/projects/milestones/list')
    } finally {
      setMilestoneDataLoading(false)
    }
  }

  const populateFormData = (data) => {
    // Extract IDs from populated objects
    let projectValue = ''
    if (data.project) {
      if (typeof data.project === 'object' && data.project !== null) {
        projectValue = String(data.project._id || data.project.id || '')
      } else {
        projectValue = String(data.project)
      }
    }

    let assignedToValue = ''
    if (data.assignedTo) {
      if (typeof data.assignedTo === 'object' && data.assignedTo !== null) {
        assignedToValue = String(data.assignedTo._id || data.assignedTo.id || '')
      } else {
        assignedToValue = String(data.assignedTo)
      }
    }

    let priorityValue = ''
    if (data.priority) {
      if (typeof data.priority === 'object' && data.priority !== null) {
        priorityValue = String(data.priority._id || data.priority.id || '')
      } else {
        priorityValue = String(data.priority)
      }
    }

    let parentMilestoneValue = ''
    if (data.parentMilestone) {
      if (typeof data.parentMilestone === 'object' && data.parentMilestone !== null) {
        parentMilestoneValue = String(data.parentMilestone._id || data.parentMilestone.id || '')
      } else {
        parentMilestoneValue = String(data.parentMilestone)
      }
    }

    let approverValue = ''
    if (data.approver) {
      if (typeof data.approver === 'object' && data.approver !== null) {
        approverValue = String(data.approver._id || data.approver.id || '')
      } else {
        approverValue = String(data.approver)
      }
    }

    // Extract dependencies
    const dependenciesValue = Array.isArray(data.dependencies)
      ? data.dependencies.map(dep => {
          if (typeof dep === 'object' && dep !== null) {
            return String(dep._id || dep.id || '')
          }
          return String(dep)
        })
      : []

    // Format dates
    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toISOString().split('T')[0]
    }

    setFormData({
      milestoneNumber: data.milestoneNumber || '',
      milestoneName: data.milestoneName || '',
      milestoneDescription: data.milestoneDescription || '',
      project: projectValue,
      assignedTo: assignedToValue,
      status: data.status || 'Not Started',
      priority: priorityValue,
      plannedDate: formatDate(data.plannedDate),
      actualDate: formatDate(data.actualDate),
      startDate: formatDate(data.startDate),
      completionDate: formatDate(data.completionDate),
      progressPercentage: data.progressPercentage || 0,
      estimatedHours: data.estimatedHours || 0,
      actualHours: data.actualHours || 0,
      isBillable: data.isBillable !== undefined ? data.isBillable : true,
      billingAmount: data.billingAmount || 0,
      currency: data.currency || 'INR',
      invoicedAmount: data.invoicedAmount || 0,
      paidAmount: data.paidAmount || 0,
      dependencies: dependenciesValue,
      parentMilestone: parentMilestoneValue,
      deliverables: Array.isArray(data.deliverables) ? data.deliverables : [],
      acceptanceCriteria: Array.isArray(data.acceptanceCriteria) ? data.acceptanceCriteria : [],
      notes: data.notes || '',
      requiresApproval: data.requiresApproval || false,
      approver: approverValue,
      riskLevel: data.riskLevel || 'Low'
    })
  }

  const fetchNextMilestoneNumber = async () => {
    try {
      setIsLoadingMilestoneNumber(true)
      const response = await autoIncrementService.getCurrentValue('MILESTONE')
      if (response.success && response.data) {
        const sequence = response.data
        if (sequence.formattedValue) {
          setFormData(prev => ({
            ...prev,
            milestoneNumber: sequence.formattedValue
          }))
        } else {
          setFormData(prev => ({
            ...prev,
            milestoneNumber: 'MIL-000001'
          }))
        }
      } else {
        setFormData(prev => ({
          ...prev,
          milestoneNumber: 'MIL-000001'
        }))
      }
    } catch (error) {
      console.error('Error fetching next milestone number:', error)
      setFormData(prev => ({
        ...prev,
        milestoneNumber: 'MIL-000001'
      }))
    } finally {
      setIsLoadingMilestoneNumber(false)
    }
  }

  const loadMasterData = async () => {
    try {
      setLoading(true)
      const [
        projectsRes,
        employeesRes,
        prioritiesRes,
        milestonesRes
      ] = await Promise.allSettled([
        projectService.getAllProjects({ limit: 1000, status: 'Active' }),
        employeeMasterService.getAllEmployees({ limit: 1000, status: 'Active' }),
        priorityMasterService.getAllPriorityMasters({ limit: 1000 }),
        milestoneService.getAllMilestones({ limit: 1000 })
      ])

      const getData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
          if (res.projects) {
            return res.projects
          }
          if (res.milestones) {
            return res.milestones
          }
        }
        return []
      }

      const getEmployeesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
        }
        return []
      }

      const getPrioritiesData = (result) => {
        if (result.status === 'fulfilled' && result.value) {
          const res = result.value
          if (res.success !== false && res.data) {
            return res.data
          }
          if (Array.isArray(res)) {
            return res
          }
        }
        return []
      }

      setMasterData({
        projects: getData(projectsRes),
        employees: getEmployeesData(employeesRes),
        priorities: getPrioritiesData(prioritiesRes),
        milestones: getData(milestonesRes)
      })

      const failedRequests = [
        { name: 'Projects', result: projectsRes },
        { name: 'Employees', result: employeesRes },
        { name: 'Priorities', result: prioritiesRes },
        { name: 'Milestones', result: milestonesRes }
      ].filter(item => item.result.status === 'rejected')

      if (failedRequests.length > 0) {
        console.warn('Some master data failed to load:', failedRequests.map(r => r.name))
        const hasNetworkError = failedRequests.some(r => {
          const error = r.result.reason
          return error && (error._errorHandled || error.request)
        })
        if (!hasNetworkError) {
          if (failedRequests.length === 4) {
            toast.error('Unable to connect to backend server. Please ensure the server is running.')
          } else {
            toast.warning(`Failed to load some data: ${failedRequests.map(r => r.name).join(', ')}`)
          }
        }
      }
      
      setMasterDataLoaded(true)
    } catch (error) {
      console.error('Error loading master data:', error)
      if (!error._errorHandled && !error.request) {
        toast.error('Failed to load master data. Please check your connection.')
      }
      setMasterDataLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const handleAddDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, newDeliverable.trim()]
      }))
      setNewDeliverable('')
    }
  }

  const handleRemoveDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }))
  }

  const handleAddAcceptanceCriteria = () => {
    if (newAcceptanceCriteria.trim()) {
      setFormData(prev => ({
        ...prev,
        acceptanceCriteria: [...prev.acceptanceCriteria, newAcceptanceCriteria.trim()]
      }))
      setNewAcceptanceCriteria('')
    }
  }

  const handleRemoveAcceptanceCriteria = (index) => {
    setFormData(prev => ({
      ...prev,
      acceptanceCriteria: prev.acceptanceCriteria.filter((_, i) => i !== index)
    }))
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.milestoneName || formData.milestoneName.trim() === '') {
      newErrors.milestoneName = 'Milestone name is required'
    }
    if (!formData.project) {
      newErrors.project = 'Project is required'
    }
    if (!formData.plannedDate) {
      newErrors.plannedDate = 'Planned date is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    try {
      setLoading(true)

      let response
      if (isEditMode) {
        const { milestoneNumber, ...updateData } = formData
        response = await milestoneService.updateMilestone(milestoneId, updateData)
      } else {
        const { milestoneNumber, ...createData } = formData
        response = await milestoneService.createMilestone(createData)
      }

      if (response.success) {
        toast.success(`Milestone ${isEditMode ? 'updated' : 'created'} successfully`)
        navigate('/projects/milestones/list')
      } else {
        toast.error(response.message || `Failed to ${isEditMode ? 'update' : 'create'} milestone`)
      }
    } catch (error) {
      console.error('Error saving milestone:', error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} milestone`)
    } finally {
      setLoading(false)
    }
  }

  const handleTestData = () => {
    const today = new Date()
    const plannedDate = new Date(today)
    plannedDate.setDate(today.getDate() + 30)

    setFormData(prev => ({
      ...prev,
      milestoneNumber: prev.milestoneNumber, // Preserve existing milestoneNumber
      milestoneName: 'Phase 1 Completion',
      milestoneDescription: 'Complete Phase 1 development and testing',
      project: masterData.projects.length > 0 ? (masterData.projects[0]._id || masterData.projects[0].id) : '',
      assignedTo: masterData.employees.length > 0 ? (masterData.employees[0]._id || masterData.employees[0].id) : '',
      status: 'In Progress',
      priority: masterData.priorities.length > 0 ? (masterData.priorities[0]._id || masterData.priorities[0].id) : '',
      plannedDate: plannedDate.toISOString().split('T')[0],
      startDate: today.toISOString().split('T')[0],
      progressPercentage: 45,
      estimatedHours: 160,
      actualHours: 72,
      isBillable: true,
      billingAmount: 50000,
      currency: 'INR',
      deliverables: ['Module 1', 'Module 2', 'Test Reports'],
      acceptanceCriteria: ['All tests passing', 'Code review completed', 'Documentation updated'],
      notes: 'Test milestone data',
      riskLevel: 'Medium'
    }))
    setErrors({})
    toast.success('Test data loaded successfully!')
  }

  // Listen for F1 key to populate test data (only in create mode)
  useEffect(() => {
    if (isEditMode || isViewMode) return // Don't add listener in edit/view mode

    const handleKeyPress = (event) => {
      // Check if F1 key is pressed (keyCode 112 or key === 'F1')
      if (event.key === 'F1' || event.keyCode === 112) {
        event.preventDefault() // Prevent browser help menu
        handleTestData()
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyPress)

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isEditMode, isViewMode, masterData]) // Include dependencies used by handleTestData

  const breadcrumbItems = customBreadcrumbItems || [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Milestones', path: '/projects/milestones/list', onClick: () => navigate('/projects/milestones/list') },
    { label: isEditMode ? 'Edit Milestone' : isViewMode ? 'View Milestone' : 'Create New', path: isEditMode ? `/projects/milestones/edit/${milestoneId}` : isViewMode ? `/projects/milestones/view/${milestoneId}` : '/projects/milestones/new', isCurrent: true }
  ]

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: Target },
    { id: 'dates', label: 'Dates & Progress', icon: Calendar },
    { id: 'deliverables', label: 'Deliverables', icon: FileText },
    { id: 'billing', label: 'Billing', icon: DollarSign },
    { id: 'additional', label: 'Additional Info', icon: AlertCircle }
  ]

  const helpContent = (
    <>
      <h3>What is Milestone Entry?</h3>
      <p>Milestone Entry lets you <strong>create and manage project milestones</strong> for deliverable tracking. You link a milestone to a project, set name, due date, status, description, completion, and optional billing amount.</p>
      <h3>Tabs</h3>
      <p><strong>Basic Information</strong> – Milestone number, name, project, status, priority, description.</p>
      <p><strong>Dates &amp; Progress</strong> – Due date, completed date, progress percentage.</p>
      <p><strong>Deliverables</strong> – Deliverable description and acceptance criteria.</p>
      <p><strong>Billing</strong> – Billable flag and amount for revenue recognition.</p>
      <p><strong>Additional Info</strong> – Notes and optional fields.</p>
      <h3>How to use</h3>
      <p>Select a <strong>project</strong>, enter milestone name and due date. Use <strong>Create Milestone</strong> to save. Track progress and mark complete when done.</p>
    </>
  )

  // Filter milestones by selected project (for dependencies and parent)
  const filteredMilestones = formData.project
    ? masterData.milestones.filter(milestone => {
        const milestoneProjectId = milestone.project?._id || milestone.project
        const milestoneId = milestone._id || milestone.id
        // Exclude current milestone if in edit mode
        if (isEditMode && milestoneId === milestoneId) {
          return false
        }
        return String(milestoneProjectId) === String(formData.project)
      })
    : []

  if (milestoneDataLoading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading milestone data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Milestone Entry</h1>
          <p>Create and manage milestone information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Milestone Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            )
          })}
        </div>

        <form className="form-content" onSubmit={handleSubmit}>
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div style={{
              padding: '12px 16px',
              background: '#fee2e2',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <AlertCircle size={16} style={{ color: '#DC2626' }} />
                <strong style={{ color: '#DC2626' }}>Please fix the following errors:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#DC2626' }}>
                {Object.entries(errors).map(([field, message]) => {
                  const fieldNames = {
                    milestoneName: 'Milestone Name',
                    project: 'Project',
                    plannedDate: 'Planned Date'
                  }
                  return (
                    <li key={field} style={{ marginBottom: '4px', cursor: 'pointer' }}
                        onClick={() => {
                          const element = document.getElementById(field) || document.querySelector(`[name="${field}"]`)
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            element.focus()
                          }
                        }}>
                      <strong>{fieldNames[field] || field}:</strong> {message}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'basic' && (
              <div>
                <div className="section-header">
                  <h3>Basic Information</h3>
                </div>
                
                {/* Row 1: Milestone Number, Milestone Name, Project */}
                <div className="grid-row-3">
                  {/* Milestone Number */}
                  <div className="form-group">
                    <label htmlFor="milestoneNumber" className="form-label">
                      Milestone Number
                    </label>
                    <input
                      id="milestoneNumber"
                      type="text"
                      value={isLoadingMilestoneNumber ? 'Loading...' : formData.milestoneNumber}
                      className="form-input"
                      placeholder="Auto-generated"
                      readOnly
                      disabled
                      style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>

                  {/* Milestone Name */}
                  <div className="form-group">
                    <label htmlFor="milestoneName" className="form-label required">
                      Milestone Name
                    </label>
                    <input
                      id="milestoneName"
                      type="text"
                      value={formData.milestoneName}
                      onChange={(e) => handleInputChange('milestoneName', e.target.value)}
                      className="form-input"
                      placeholder="Enter milestone name"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.milestoneName && (
                      <p className="form-error">{errors.milestoneName}</p>
                    )}
                  </div>

                  {/* Project */}
                  <div className="form-group">
                    <label htmlFor="project" className="form-label required">
                      Project
                    </label>
                    <select
                      id="project"
                      value={formData.project}
                      onChange={(e) => handleInputChange('project', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Project</option>
                      {masterData.projects.map((project) => {
                        const projectId = project._id || project.id
                        return (
                          <option key={projectId} value={String(projectId)}>
                            {project.projectNumber} - {project.projectName}
                          </option>
                        )
                      })}
                    </select>
                    {errors.project && (
                      <p className="form-error">{errors.project}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Description */}
                <div className="form-group">
                  <label htmlFor="milestoneDescription" className="form-label">
                    Milestone Description
                  </label>
                  <textarea
                    id="milestoneDescription"
                    value={formData.milestoneDescription}
                    onChange={(e) => handleInputChange('milestoneDescription', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter milestone description"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Row 3: Assigned To, Status, Priority */}
                <div className="grid-row-3">
                  {/* Assigned To */}
                  <div className="form-group">
                    <label htmlFor="assignedTo" className="form-label">
                      Assigned To
                    </label>
                    <select
                      id="assignedTo"
                      value={formData.assignedTo}
                      onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Employee</option>
                      {masterData.employees.map((employee) => {
                        const employeeId = employee._id || employee.id
                        return (
                          <option key={employeeId} value={String(employeeId)}>
                            {employee.employeeName || employee.name}
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Not Started">Not Started</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                  </div>

                  {/* Priority */}
                  <div className="form-group">
                    <label htmlFor="priority" className="form-label">
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => handleInputChange('priority', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Priority</option>
                      {masterData.priorities.map((priority) => {
                        const priorityId = priority._id || priority.id
                        return (
                          <option key={priorityId} value={String(priorityId)}>
                            {priority.priorityName || priority.name}
                          </option>
                        )
                      })}
                    </select>
                  </div>
                </div>

                {/* Row 4: Parent Milestone, Risk Level */}
                <div className="grid-row-3">
                  {/* Parent Milestone */}
                  <div className="form-group">
                    <label htmlFor="parentMilestone" className="form-label">
                      Parent Milestone
                    </label>
                    <select
                      id="parentMilestone"
                      value={formData.parentMilestone}
                      onChange={(e) => handleInputChange('parentMilestone', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly || !formData.project}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="">Select Parent Milestone</option>
                      {filteredMilestones.map((milestone) => {
                        const milestoneId = milestone._id || milestone.id
                        return (
                          <option key={milestoneId} value={String(milestoneId)}>
                            {milestone.milestoneNumber} - {milestone.milestoneName}
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  {/* Risk Level */}
                  <div className="form-group">
                    <label htmlFor="riskLevel" className="form-label">
                      Risk Level
                    </label>
                    <select
                      id="riskLevel"
                      value={formData.riskLevel}
                      onChange={(e) => handleInputChange('riskLevel', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'dates' && (
              <div>
                <div className="section-header">
                  <h3>Dates & Progress</h3>
                </div>
                
                {/* Row 1: Planned Date, Actual Date, Start Date */}
                <div className="grid-row-3">
                  {/* Planned Date */}
                  <div className="form-group">
                    <label htmlFor="plannedDate" className="form-label required">
                      Planned Date
                    </label>
                    <input
                      id="plannedDate"
                      type="date"
                      value={formData.plannedDate}
                      onChange={(e) => handleInputChange('plannedDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {errors.plannedDate && (
                      <p className="form-error">{errors.plannedDate}</p>
                    )}
                  </div>

                  {/* Actual Date */}
                  <div className="form-group">
                    <label htmlFor="actualDate" className="form-label">
                      Actual Date
                    </label>
                    <input
                      id="actualDate"
                      type="date"
                      value={formData.actualDate}
                      onChange={(e) => handleInputChange('actualDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Start Date */}
                  <div className="form-group">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                {/* Row 2: Completion Date, Progress Percentage */}
                <div className="grid-row-3">
                  {/* Completion Date */}
                  <div className="form-group">
                    <label htmlFor="completionDate" className="form-label">
                      Completion Date
                    </label>
                    <input
                      id="completionDate"
                      type="date"
                      value={formData.completionDate}
                      onChange={(e) => handleInputChange('completionDate', e.target.value)}
                      className="form-input"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Progress Percentage */}
                  <div className="form-group">
                    <label htmlFor="progressPercentage" className="form-label">
                      Progress Percentage
                    </label>
                    <input
                      id="progressPercentage"
                      type="number"
                      value={formData.progressPercentage}
                      onChange={(e) => handleInputChange('progressPercentage', Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                      className="form-input"
                      min="0"
                      max="100"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>

                {/* Row 3: Estimated Hours, Actual Hours */}
                <div className="grid-row-3">
                  {/* Estimated Hours */}
                  <div className="form-group">
                    <label htmlFor="estimatedHours" className="form-label">
                      Estimated Hours
                    </label>
                    <input
                      id="estimatedHours"
                      type="number"
                      value={formData.estimatedHours}
                      onChange={(e) => handleInputChange('estimatedHours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.5"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>

                  {/* Actual Hours */}
                  <div className="form-group">
                    <label htmlFor="actualHours" className="form-label">
                      Actual Hours
                    </label>
                    <input
                      id="actualHours"
                      type="number"
                      value={formData.actualHours}
                      onChange={(e) => handleInputChange('actualHours', parseFloat(e.target.value) || 0)}
                      className="form-input"
                      min="0"
                      step="0.5"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'deliverables' && (
              <div>
                <div className="section-header">
                  <h3>Deliverables & Acceptance Criteria</h3>
                </div>
                
                {/* Deliverables */}
                <div className="form-group">
                  <label className="form-label">Deliverables</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={newDeliverable}
                      onChange={(e) => setNewDeliverable(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddDeliverable()
                        }
                      }}
                      className="form-input"
                      placeholder="Enter deliverable"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={handleAddDeliverable}
                        className="btn-secondary"
                        style={{ minWidth: '80px' }}
                      >
                        Add
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {formData.deliverables.map((deliverable, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                        <span style={{ flex: 1 }}>{deliverable}</span>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveDeliverable(index)}
                            className="btn-secondary"
                            style={{ padding: '4px 8px', minWidth: 'auto' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Acceptance Criteria */}
                <div className="form-group">
                  <label className="form-label">Acceptance Criteria</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input
                      type="text"
                      value={newAcceptanceCriteria}
                      onChange={(e) => setNewAcceptanceCriteria(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddAcceptanceCriteria()
                        }
                      }}
                      className="form-input"
                      placeholder="Enter acceptance criteria"
                      disabled={isReadOnly}
                      style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                    />
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={handleAddAcceptanceCriteria}
                        className="btn-secondary"
                        style={{ minWidth: '80px' }}
                      >
                        Add
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {formData.acceptanceCriteria.map((criteria, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                        <span style={{ flex: 1 }}>{criteria}</span>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAcceptanceCriteria(index)}
                            className="btn-secondary"
                            style={{ padding: '4px 8px', minWidth: 'auto' }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'billing' && (
              <div>
                <div className="section-header">
                  <h3>Billing Information</h3>
                </div>
                
                {/* Row 1: Is Billable, Billing Amount, Currency */}
                <div className="grid-row-3">
                  {/* Is Billable */}
                  <div className="form-group">
                    <label htmlFor="isBillable" className="form-label">
                      <input
                        type="checkbox"
                        id="isBillable"
                        checked={formData.isBillable}
                        onChange={(e) => handleInputChange('isBillable', e.target.checked)}
                        disabled={isReadOnly}
                        style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                      />
                      Is Billable
                    </label>
                  </div>

                  {/* Billing Amount */}
                  {formData.isBillable && (
                    <>
                      <div className="form-group">
                        <label htmlFor="billingAmount" className="form-label">
                          Billing Amount
                        </label>
                        <input
                          id="billingAmount"
                          type="number"
                          value={formData.billingAmount}
                          onChange={(e) => handleInputChange('billingAmount', parseFloat(e.target.value) || 0)}
                          className="form-input"
                          min="0"
                          step="0.01"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="currency" className="form-label">
                          Currency
                        </label>
                        <select
                          id="currency"
                          value={formData.currency}
                          onChange={(e) => handleInputChange('currency', e.target.value)}
                          className="form-input"
                          disabled={isReadOnly}
                          style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                        >
                          <option value="INR">INR</option>
                          <option value="USD">USD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                          <option value="CAD">CAD</option>
                          <option value="AUD">AUD</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>

                {/* Row 2: Invoiced Amount, Paid Amount */}
                {formData.isBillable && (
                  <div className="grid-row-3">
                    {/* Invoiced Amount */}
                    <div className="form-group">
                      <label htmlFor="invoicedAmount" className="form-label">
                        Invoiced Amount
                      </label>
                      <input
                        id="invoicedAmount"
                        type="number"
                        value={formData.invoicedAmount}
                        onChange={(e) => handleInputChange('invoicedAmount', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                        disabled={isReadOnly}
                        style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                      />
                    </div>

                    {/* Paid Amount */}
                    <div className="form-group">
                      <label htmlFor="paidAmount" className="form-label">
                        Paid Amount
                      </label>
                      <input
                        id="paidAmount"
                        type="number"
                        value={formData.paidAmount}
                        onChange={(e) => handleInputChange('paidAmount', parseFloat(e.target.value) || 0)}
                        className="form-input"
                        min="0"
                        step="0.01"
                        disabled={isReadOnly}
                        style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'additional' && (
              <div>
                <div className="section-header">
                  <h3>Additional Information</h3>
                </div>
                
                {/* Notes */}
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter additional notes"
                    readOnly={isReadOnly}
                    disabled={isReadOnly}
                    style={isReadOnly ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>

                {/* Requires Approval */}
                <div className="form-group">
                  <label htmlFor="requiresApproval" className="form-label">
                    <input
                      type="checkbox"
                      id="requiresApproval"
                      checked={formData.requiresApproval}
                      onChange={(e) => handleInputChange('requiresApproval', e.target.checked)}
                      disabled={isReadOnly}
                      style={isReadOnly ? { cursor: 'not-allowed' } : {}}
                    />
                    Requires Approval
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          {!isViewMode && (
            <div className="form-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => navigate('/projects/milestones/list')}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : isEditMode ? 'Update Milestone' : 'Create Milestone'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Milestone Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default MilestoneForm

